import { clamp } from "./maths";

export const adderOperationTypes = [
  "max",
  "goto",
  "set",
  "multiply",
  "add",
] as const;
export type AdderOperationType = (typeof adderOperationTypes)[number];

export type AdderOperation = {
  type: AdderOperationType;
  value: number;
};

export class Adder {
  private _index: number = -1;
  private readonly operations: AdderOperation[];
  private readonly pastStates: { index: number; value: number }[] = [];

  constructor(...operations: string[]) {
    this.operations = operations.map((operation) =>
      Adder.parseOperation(operation),
    );
  }

  static parseOperationType(operation: string): AdderOperationType {
    if (operation === "max") {
      return "max";
    }

    const prefix = operation[0];

    switch (prefix) {
      case "g":
        return "goto";

      case "=":
        return "set";

      case "x":
        return "multiply";

      case "+":
        return "add";

      default:
        throw new SyntaxError(
          `Invalid adder operation type prefix: ${operation}`,
        );
    }
  }

  static parseOperation(operation: string): AdderOperation {
    const type = Adder.parseOperationType(operation);

    if (type === "max") {
      return { type, value: NaN };
    }

    const rawValue = operation.slice(1);

    const value = Number.parseFloat(rawValue);

    if (Number.isNaN(value)) {
      throw new SyntaxError(`Invalid adder operation value: ${rawValue}`);
    }

    return { type, value };
  }

  get index() {
    return this._index;
  }

  get length() {
    return this.operations.length;
  }

  get pastStatesCount(): number {
    return this.pastStates.length;
  }

  get hasPastStates(): boolean {
    return this.pastStatesCount > 0;
  }

  next(value: number, max: number = Infinity) {
    this.pastStates.push({ index: this._index, value });

    this._index = clamp(this.index + 1, 0, this.length - 1);

    let iterationCount = 0;

    while (this.operations[this.index].type === "goto") {
      this._index = clamp(
        this.operations[this.index].value,
        0,
        this.length - 1,
      );

      if (iterationCount++ > 10) {
        throw new RangeError(`Too many iterations with type "goto" in adder`);
      }
    }

    const operation = this.operations[this.index];

    if (operation.type === "max") {
      value = max;
    } else if (operation.type === "set") {
      value = operation.value;
    } else if (operation.type === "multiply") {
      value *= operation.value;
    } else if (operation.type === "add") {
      value += operation.value;
    }

    return clamp(value, 0, max);
  }

  previous(max: number = Infinity): number {
    const state = this.pastStates.pop();

    if (!state) {
      throw new Error("No previous state");
    }

    this._index = state.index;

    return clamp(state.value, 0, max);
  }

  reset(): void {
    this._index = -1;
    this.pastStates.length = 0;
  }
}
